import posthog from 'posthog-js';

// Single source of truth for product analytics. Everything funnels through
// here so we can enforce two rules in one place:
//   1. Nothing is captured until we explicitly opt in (identify, or the signup
//      flow). Anonymous browsing never enters our event volume.
//   2. Event names + super properties stay consistent across 4casters / 4cx.
//
// 4CX mirrors this file with PLATFORM = '4cx'.

let initialized = false;
// Dedupe the KYC gate: it can be triggered from several entry points (login,
// account menu, mobile menu, withdrawal, app load) and re-checked on every
// getMe, but it's the same "user hit the KYC wall" event. Count it once per
// page session. Reset on logout so a different user on the same tab re-counts.
let kycRequiredTracked = false;
// Set when the route guard below tore down an in-flight replay because the
// user navigated onto a credential-bearing route. Recording is armed by
// init/optIn/identify only, none of which re-run on an SPA navigation, so
// without this the suppression would be permanent for the rest of the
// session. It records that the ROUTE stopped recording, so leaving the route
// restores exactly what the route took away and nothing else.
let recordingSuppressedByRoute = false;
// Track the last final KYC outcome we reported so routine verification polls
// (login + app mount both call verifyUserKYC) don't re-emit the same result.
let lastKycResult = null;

const PLATFORM = '4casters';

// Marker for an email_verified that was earned while the device wasn't opted in
// (verify link opened logged out). The verify token is single-use, so the
// confirm page can't re-fire the event on reload — we park it here and flush it
// on the next identify(), which is the same person logging in (one browser =
// one user). Consumed (cleared) on flush so it can't double-fire.
const PENDING_EMAIL_VERIFIED_KEY = 'email_verified_pending';

// Fire a parked email_verified once the user is identified/opted in, then clear
// the marker so it only sends once. Called from identify().
// Banner impressions that no-opped pre-identify (see captureBannerViewed).
const pendingBannerViews = new Set();

function flushPendingBannerViews() {
  for (const banner of [...pendingBannerViews]) {
    pendingBannerViews.delete(banner);
    PostHogUtils.captureBannerViewed(banner);
  }
}

function flushPendingEmailVerified() {
  try {
    if (localStorage.getItem(PENDING_EMAIL_VERIFIED_KEY) !== '1') return;
    if (!initialized || !posthog.has_opted_in_capturing()) return;
    posthog.capture('email_verified');
    localStorage.removeItem(PENDING_EMAIL_VERIFIED_KEY);
  } catch {
    // localStorage unavailable — nothing parked to flush.
  }
}

// Route-shape test for the dynamic /:sport/:league sportsbook route, used to
// classify SEO landings. First path segments that belong to a real
// static/dynamic route are excluded so they are never mistaken for a
// sport/league page. Keep in sync with the non-sport top-level routes in
// router/index.js.
const STATIC_FIRST_SEGMENTS = new Set([
  'exchange',
  'sportsbook',
  'account',
  'combined-bets',
  'my-bets',
  'marketmaker-my-bets',
  'sign-up',
  'exchange-single',
  'service', // /service/reset-password, /service/confirm-email, etc.
  'r', // discreet marketing links (/r/:code)
  'blog', // /blog/:slug
]);

function isSportLeaguePath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return false;
  const segments = pathname.split('/').filter(Boolean);
  return segments.length === 2 && !STATIC_FIRST_SEGMENTS.has(segments[0]);
}

// Account-recovery credentials that ride in our own URLs: /service/reset-password
// takes `token`, /service/confirm-email takes `tokenaccount`. Both are usable
// until they expire, so they must never be stored in analytics — they would land
// on events, and permanently on the person profile via $initial_current_url.
// These two names are read nowhere else in the app (every other query parameter
// is search/sport/action/redirect), so removing them by name costs no data.
const CREDENTIAL_PARAMS = ['token', 'tokenaccount'];

// URL-valued properties posthog-js stamps on EVERY event, not just pageviews.
const URL_PROPERTY_KEYS = ['$current_url', '$referrer', '$initial_referrer', '$initial_current_url'];

// Strip credentials from a URL-valued property. Scoped to our own host: only
// our URLs can carry our tokens, and inbound URLs are the whole point of
// attribution — an external referrer keeps its query string untouched, so
// campaign tags and partner parameters always survive intact.
function stripCredentials(value) {
  try {
    const url = new URL(value);
    if (url.hostname !== window.location.hostname) return value;
    let changed = false;
    for (const param of CREDENTIAL_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        changed = true;
      }
    }
    return changed ? url.toString() : value;
  } catch {
    // Non-URL values ("$direct") and anything unparseable pass through.
    return value;
  }
}

// Session replay stores the page address in its own snapshot payload, which
// before_send never inspects — so stripping credentials from event properties
// does not keep a reset link's token out of a replay. Recording is therefore
// suppressed entirely on the routes that carry credentials: the same boundary
// the tokens live behind, so there is no second list to keep in sync.
function isServiceRoute(path) {
  return typeof path === 'string' && /^\/service(\/|$)/.test(path);
}

function onCredentialRoute() {
  try {
    return isServiceRoute(window.location.pathname);
  } catch {
    return false;
  }
}

function startSessionRecordingUnlessCredentialRoute() {
  if (onCredentialRoute()) {
    // Mark the suppression where it actually happens, not where the navigation
    // happens. An emailed reset/confirm link is a COLD LOAD: it reaches here
    // via init and identify and never runs the router's onto-the-route branch,
    // so this is the only place that catches that shape. The first navigation
    // off the route then restores replay through the same leave-path.
    if (posthog.has_opted_in_capturing()) recordingSuppressedByRoute = true;
    return;
  }
  posthog.startSessionRecording();
}

function stripCredentialProperties(properties) {
  if (!properties) return properties;
  for (const key of URL_PROPERTY_KEYS) {
    if (typeof properties[key] === 'string') {
      properties[key] = stripCredentials(properties[key]);
    }
  }
  // Person-property payloads carry the $initial_* URL props too, and those
  // persist on the profile rather than expiring with the event stream.
  for (const bucket of ['$set', '$set_once']) {
    const obj = properties[bucket];
    if (obj && typeof obj === 'object') {
      for (const key of URL_PROPERTY_KEYS) {
        if (typeof obj[key] === 'string') {
          obj[key] = stripCredentials(obj[key]);
        }
      }
    }
  }
  return properties;
}

// ---- SEO landing tracking (PRO-118) ----
// Anonymous visitors landing on public CONTENT routes (sport pages, game
// pages, blog) are opted into event capture so organic search traffic is
// measurable — the SEO audience is logged-out by definition. Scope rules:
//   * Only a content-route landing triggers the opt-in; landing anywhere else
//     leaves the visitor untracked as before.
//   * Session replay is NOT started for these visitors — events only. The
//     signup flow's optIn()/identify() still enables recording as before.
// Anonymous capture is ON, in COOKIELESS mode (PM decision, 2026-07-21): we
// do serve EU visitors, so anonymous tracking stores NOTHING on the device —
// persistence is switched to memory before the anonymous opt-in, which
// sidesteps the ePrivacy consent question entirely. Accepted trade: an
// anonymous visitor returning later counts as a new visitor. The signup/login
// paths restore durable persistence (existing behavior).
const ANON_CAPTURE_ENABLED = true;

const SEO_CONTENT_PATTERNS = [/^\/exchange-single\/[^/]+\/?$/, /^\/blog(\/|$)/];

function isSeoContentPath(path) {
  if (SEO_CONTENT_PATTERNS.some((p) => p.test(path))) return true;
  return isSportLeaguePath(path);
}

// TLD patterns are bounded ({2,3} + optional country suffix) so a hostname
// like google.evil.com can't spoof organic attribution.
const SEARCH_ENGINE_HOSTS = [
  ['google', /(^|\.)google\.[a-z]{2,3}(\.[a-z]{2})?$/],
  ['bing', /(^|\.)bing\.com$/],
  ['duckduckgo', /(^|\.)duckduckgo\.com$/],
  ['yahoo', /(^|\.)yahoo\.[a-z]{2,3}(\.[a-z]{2})?$/],
  ['brave', /(^|\.)search\.brave\.com$/],
  ['ecosia', /(^|\.)ecosia\.org$/],
];

// Classify document.referrer: organic (search engine), internal, referral,
// or direct (empty/unparseable).
function referrerInfo() {
  try {
    if (!document.referrer) return { source: 'direct', engine: null };
    const host = new URL(document.referrer).hostname;
    if (host === window.location.hostname) return { source: 'internal', engine: null };
    const hit = SEARCH_ENGINE_HOSTS.find(([, re]) => re.test(host));
    if (hit) return { source: 'organic', engine: hit[0] };
    return { source: 'referral', engine: null };
  } catch {
    return { source: 'direct', engine: null };
  }
}

// Landing labels keep the league/slug — bounded cardinality, and knowing
// WHICH page earns arrivals is the point. Only game ids are collapsed.
function landingPathLabel(path) {
  if (/^\/exchange-single\//.test(path)) return '/exchange-single/:gameID';
  return path.replace(/\/$/, '') || '/';
}

function landingType(path) {
  if (/^\/blog(\/|$)/.test(path)) return 'blog';
  if (/^\/exchange-single\//.test(path)) return 'game';
  if (isSportLeaguePath(path)) return 'sport';
  return 'other';
}

// The first navigation of the page session decides the landing exactly once.
// Backed by sessionStorage (tab-scoped, gone on close — not persistent device
// storage) so a mid-session reload can't double-count seo_landing; module flag
// is the fallback when storage is unavailable.
const SEO_LANDING_SS_KEY = 'seo_landing_handled';
let seoLandingHandled = false;

function landingAlreadyHandled() {
  try {
    return seoLandingHandled || sessionStorage.getItem(SEO_LANDING_SS_KEY) === '1';
  } catch {
    return seoLandingHandled;
  }
}

function markLandingHandled() {
  seoLandingHandled = true;
  try {
    sessionStorage.setItem(SEO_LANDING_SS_KEY, '1');
  } catch {
    // Storage unavailable — the module flag still dedupes within this load.
  }
}

// First-touch landing carried in memory so identify() can stamp it onto the
// person even though the anonymous session used memory-only persistence
// (super properties don't survive the persistence switch at signup).
let pendingFirstLanding = null;

// The 6-hourly snapshot renderer (and any automated browser) loads pages as
// an anonymous visitor — it must never initialize analytics or it inflates
// every SEO metric it exists to measure.
function isAutomatedBrowser() {
  try {
    return navigator.webdriver === true || /4cSeoRenderer/.test(navigator.userAgent);
  } catch {
    return false;
  }
}

// A device whose posthog persistence says the user was identified belongs to
// a known account — that state lives in localStorage, so it's readable even
// while the app's auth state is still rehydrating on the first navigation.
// It matters because posthog stores the opt-in/out consent flag in its own
// localStorage key REGARDLESS of the persistence mode, so consent alone
// can't tell a returning logged-in device from an anonymous cookieless
// visitor whose consent outlived their memory-only session.
function isIdentifiedDevice() {
  return posthog.get_property('$user_state') === 'identified';
}

// Keep saved settings values in one consistent shape so a single real setting
// can't show up as several look-alike values (e.g. "yes" / "true" / true).
// Booleans stay booleans, finite numbers stay numbers, strings get trimmed,
// and empty/missing values are dropped instead of saved as noise.
// Super properties attached to every event. Registered at init AND re-registered
// after reset(): posthog.reset() clears super properties, and logout is an SPA
// navigation that never re-runs init — without the re-register, a later login in
// the same tab would emit events missing platform/app_version/environment.
function registerSuperProperties() {
  posthog.register({
    platform: PLATFORM,
    app_version:
      typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    environment: import.meta.env.VITE_APP_ENV || 'development',
  });
}

function cleanSettings(settings) {
  const cleaned = {};
  for (const [key, value] of Object.entries(settings)) {
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    cleaned[key] = typeof value === 'string' ? value.trim() : value;
  }
  return cleaned;
}

// Resolve the "no funds to bet" flag for bet_modal_opened. Uses *spendable
// availability*, the same quantity the bet modals gate on
// (displayBalance + liability - creditLimit), not raw displayBalance — a user
// can show a positive balance but have nothing spendable. The threshold matches
// the bet modals' funding gate exactly: betting is blocked when available < 1,
// so anything below $1 counts as "empty" (a sub-$1 user is blocked too).
// Returns:
//   true  — logged in with known availability < 1 (UI would block funding)
//   false — logged in with known availability >= 1
//   null  — not logged in, or any input unknown/non-numeric (so we never
//           report a misleading `false` when funds are actually unknown)
export function resolveEmptyBalance(user) {
  if (!user || !user.isLoggedIn) return null;
  const displayBalance = parseFloat(user.displayBalance);
  const liability = parseFloat(user.liability);
  const creditLimit = parseFloat(user.creditLimit);
  if (
    !Number.isFinite(displayBalance) ||
    !Number.isFinite(liability) ||
    !Number.isFinite(creditLimit)
  ) {
    return null;
  }
  return displayBalance + liability - creditLimit < 1;
}

// notification_clicked.notification_type passes through raw socket/backend
// notification type names, which mix naming conventions ('orderFill' vs
// 'bet-taken') — one property, two spellings per concept. Kebab-case any
// camelCase name at capture time ('orderFill' → 'order-fill') so a single
// real type can't read as two values in breakdowns. Names already in
// kebab-case pass through unchanged — historic kebab values must keep
// matching, so existing values are never renamed here.
export function normalizeNotificationType(type) {
  if (typeof type !== 'string' || !type) return 'unknown';
  return type.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

// Normalize a search query before it goes on a `game_search_performed` event
// (only sent for no-result searches). Lowercasing + trimming + collapsing
// whitespace aggregates variants of the same query into one row in analytics,
// and the length cap bounds cardinality / avoids storing pasted walls of text.
// Returns null for empty/non-string input so we never attach a junk property.
const MAX_SEARCH_TERM_LENGTH = 60;
export function normalizeSearchTerm(raw) {
  if (typeof raw !== 'string') return null;
  const cleaned = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!cleaned) return null;
  return cleaned.slice(0, MAX_SEARCH_TERM_LENGTH);
}

export const PostHogUtils = {
  init() {
    const key = import.meta.env.VITE_POSTHOG_KEY;
    // No key configured (local dev without analytics) → stay disabled so
    // capture() / identify() become safe no-ops.
    if (!key) return;
    // The snapshot renderer / automated browsers must never feed analytics.
    if (isAutomatedBrowser()) return;

    posthog.init(key, {
      // Anonymous SEO events must not create billable person profiles.
      person_profiles: 'identified_only',
      api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
      autocapture: false,
      // 'history_change' (not boolean true) so pageviews fire on Vue Router
      // navigations, not just the initial load — this is a history-mode SPA.
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Session replay armed only by explicit startSessionRecording() calls
      // (optIn()/identify() — the signup/login paths). Disabled at init so an
      // anonymous SEO-landing opt-in can NEVER start replay, by construction
      // (a stop-after-opt-in would be a race). Signup abandonment recording
      // still works: optIn() starts it. All inputs masked so no
      // address/amount/password content ever leaves the browser; add
      // class="ph-no-capture" to hide any element wholesale.
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask',
      },
      opt_out_capturing_by_default: true,
      persistence: 'localStorage+cookie',
      before_send: (event) => {
        // Every event — custom events carry $current_url/$referrer too.
        if (event) {
          event.properties = stripCredentialProperties(event.properties);
        }
        return event;
      },
    });

    registerSuperProperties();

    // Devices that already opted into durable capture (signed up / logged in
    // before) resume session replay immediately — recording is disabled in
    // the init config and would otherwise stay off until identify() runs
    // after getMe hydration. Gated on the persisted $user_state: an anonymous
    // cookieless visitor's consent flag survives reloads too (see
    // isIdentifiedDevice), and replay must stay identified-users-only.
    if (posthog.has_opted_in_capturing() && isIdentifiedDevice()) {
      startSessionRecordingUnlessCredentialRoute();
    }

    initialized = true;
  },

  // Opt a still-anonymous visitor into capture so the registration funnel can
  // be recorded before an account exists. A later identify() links these
  // anonymous events to the created user.
  optIn() {
    if (!initialized) return;
    // Signup is durable tracking — restore device persistence in case an
    // anonymous SEO landing switched this session to memory-only.
    posthog.set_config({ persistence: 'localStorage+cookie' });
    if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing();
    // A prior SEO-landing opt-in stopped recording; the signup flow wants it.
    startSessionRecordingUnlessCredentialRoute();
  },

  // Called from the router on every navigation (PRO-118 SEO tracking).
  // Handles the cookieless anonymous opt-in for content views and the
  // once-per-session landing (first_landing_path + seo_landing for organic
  // arrivals). Game views are tracked by SingleGame's game_viewed event
  // (carrying is_logged_in), not here.
  trackContentView(path, isLoggedIn) {
    if (!initialized || typeof path !== 'string') return;

    // Navigating onto a credential-bearing route mid-session stops recording,
    // so an in-flight replay can't pick up a token from the address bar.
    if (isServiceRoute(path)) {
      if (posthog.has_opted_in_capturing()) recordingSuppressedByRoute = true;
      posthog.stopSessionRecording();
      // Consume the once-per-session landing window even though this arrival
      // is neither content nor organic: without it the NEXT page the user
      // opens would claim to be their first landing and skew first_landing_path
      // (and could emit a false seo_landing).
      markLandingHandled();
      return;
    }

    // Left the credential route — restore the replay the guard tore down.
    // Gated on the same condition init uses, so an anonymous cookieless SEO
    // visitor is never armed here; the flag means only a route-caused stop is
    // ever undone.
    if (recordingSuppressedByRoute) {
      recordingSuppressedByRoute = false;
      if (posthog.has_opted_in_capturing() && isIdentifiedDevice()) {
        startSessionRecordingUnlessCredentialRoute();
      }
    }

    const isContent = isSeoContentPath(path);
    // Persisted device check on top of the app's isLoggedIn: auth state may
    // still be rehydrating on the first navigation, and a known account's
    // device must never be switched to memory persistence (that clears its
    // durable analytics state) or counted as a new acquisition.
    const anonymousViewer =
      ANON_CAPTURE_ENABLED && !isLoggedIn && !isIdentifiedDevice();

    // Cookieless opt-in for ANY anonymous content view, not just the landing
    // one — a visitor entering via '/' and clicking into a game page is still
    // in scope. Memory persistence FIRST: nothing on the device (no cookie,
    // no localStorage) — EU/ePrivacy safe. The persistence switch is NOT
    // gated on the opt-in being fresh: posthog persists the consent flag
    // across page loads even in memory mode, so a returning anonymous
    // visitor arrives already opted in but still on the durable persistence
    // from init — they must be moved back to memory before anything is
    // captured. Recording is disabled at init and only
    // startSessionRecording() (signup/login paths) arms it, so this opt-in
    // cannot record.
    if (anonymousViewer && isContent) {
      if (posthog.config.persistence !== 'memory') {
        posthog.set_config({ persistence: 'memory' });
      }
      if (!posthog.has_opted_in_capturing()) {
        posthog.opt_in_capturing();
        // Init ran opted-out, so the landing's own $pageview was dropped —
        // send it explicitly for the fresh opt-in.
        posthog.capture('$pageview');
      }
    }

    if (!landingAlreadyHandled()) {
      markLandingHandled();
      // Acquisition metric: only logged-out arrivals count as SEO landings.
      // A logged-in user opening a sport page from search is not a new
      // organic acquisition and must not skew the funnel (anonymousViewer's
      // device check covers the window where auth is still rehydrating).
      if (anonymousViewer && isContent) {
        // Attribution anchor: every later event (registration, deposit, ...)
        // carries where this visitor first arrived; also kept in memory so
        // identify() can stamp it onto the person after the persistence
        // switch at signup wipes anonymous super properties.
        pendingFirstLanding = landingPathLabel(path);
        posthog.register_once({ first_landing_path: pendingFirstLanding });
        const ref = referrerInfo();
        if (ref.source === 'organic') {
          posthog.capture('seo_landing', {
            landing_path: landingPathLabel(path),
            search_engine: ref.engine,
            landing_type: landingType(path),
          });
        }
      }
    }

    // Game views are tracked by the SingleGame view's existing game_viewed
    // event (now carrying is_logged_in) — no separate event here.
  },

  identify(userId, props = {}) {
    if (!initialized || !userId) return;
    // Identified users get durable tracking — restore device persistence in
    // case an anonymous SEO landing switched this session to memory-only.
    posthog.set_config({ persistence: 'localStorage+cookie' });
    if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing();
    // Identified users are recorded (existing behavior) even if an earlier
    // anonymous SEO landing stopped recording on this device.
    startSessionRecordingUnlessCredentialRoute();
    posthog.identify(String(userId), props);
    // Stamp the acquisition source onto the person: the memory-persisted
    // anonymous session's super properties don't survive the persistence
    // switch above. Consumed so it can't restamp a later, unrelated login.
    // $set_once (second arg), not $set: first_landing_path is first-touch,
    // and a returning account logging in after a fresh organic landing must
    // never have its original acquisition overwritten.
    if (pendingFirstLanding) {
      posthog.setPersonProperties({}, { first_landing_path: pendingFirstLanding });
      pendingFirstLanding = null;
    }
    flushPendingEmailVerified();
    flushPendingBannerViews();
  },

  // Park an email_verified that couldn't be sent because the device wasn't
  // opted in yet (verify link opened while logged out; token is single-use so
  // checkValidation can't retry on reload). The next identify() flushes it.
  markPendingEmailVerified() {
    try {
      localStorage.setItem(PENDING_EMAIL_VERIFIED_KEY, '1');
    } catch {
      // localStorage unavailable — the event is simply lost for this verify.
    }
  },

  // Returns true if the event was actually sent, false if it was a no-op
  // (not initialized or not opted in). Callers that dedupe on send can gate
  // their dedupe flag on this so a no-op doesn't permanently suppress a later
  // real capture on the same device.
  capture(eventName, props = {}) {
    if (!initialized) return false;
    if (!posthog.has_opted_in_capturing()) return false;
    posthog.capture(eventName, props);
    return true;
  },

  // Mirror the user's current settings onto their person profile so we can
  // chart "% of users using X" without firing a per-change event.
  syncSettings(settings = {}) {
    if (!initialized) return;
    if (!posthog.has_opted_in_capturing()) return;
    const cleaned = cleanSettings(settings);
    if (Object.keys(cleaned).length) posthog.setPersonProperties(cleaned);
  },

  captureSettingChange(settingName, fromValue, toValue) {
    PostHogUtils.capture('setting_changed', {
      setting_name: settingName,
      from_value: fromValue,
      to_value: toValue,
    });
  },

  // --- Banners ----------------------------------------------------------
  // One event group for every promo/notice banner, keyed by `banner` (e.g.
  // 'referral', 'signup', 'email_verification', 'new_user'). Add a banner by
  // calling these from its component — no per-banner event names.
  //
  // Impressions dedupe once per browser session per banner (banners re-render
  // on every home visit). The sessionStorage flag is set only when the event
  // actually sends, so a no-op pre-identify capture can't suppress a later
  // real view. NOTE: anonymous visitors are opted out, so banners shown only
  // when logged out (e.g. 'signup') won't record until anonymous capture is
  // enabled — a separate policy decision.
  captureBannerViewed(banner) {
    if (!banner) return;
    const key = `banner_viewed_${banner}`;
    try {
      if (sessionStorage.getItem(key) === '1') return;
    } catch {
      return; // sessionStorage unavailable — skip rather than risk a throw
    }
    if (PostHogUtils.capture('banner_viewed', { banner })) {
      try {
        sessionStorage.setItem(key, '1');
      } catch {
        // sessionStorage unavailable — at worst one extra impression later
      }
    } else {
      // Banners mount before getMe resolves, while a returning user is still
      // opted out — the capture no-ops. Park the banner and flush it right
      // after identify() opts the device back in, so the session's first
      // home view isn't lost (bugbot).
      pendingBannerViews.add(banner);
    }
  },

  captureBannerClicked(banner, action) {
    PostHogUtils.capture('banner_clicked', { banner, action });
  },

  captureBannerDismissed(banner) {
    PostHogUtils.capture('banner_dismissed', { banner });
  },

  // Top of the KYC funnel: the user is gated by Veriff (verification required,
  // not yet approved). Centralized + deduped here because the gate fires from
  // multiple components and on repeated getMe polls.
  markKycRequired() {
    if (kycRequiredTracked) return;
    kycRequiredTracked = true;
    PostHogUtils.capture('kyc_required');
  },

  // Bottom of the KYC funnel: a final Veriff decision. verifyUserKYC runs on
  // routine session checks too, so we only emit for actual final outcomes
  // (approved/declined) and skip repeats of the same status.
  markKycResult(status) {
    if (status !== 'approved' && status !== 'declined') return;
    if (lastKycResult === status) return;
    lastKycResult = status;
    PostHogUtils.capture('kyc_result', { status });
  },

  reset() {
    if (!initialized) return;
    kycRequiredTracked = false;
    lastKycResult = null;
    pendingBannerViews.clear();
    // The per-session banner impression dedupe belongs to the user who saw
    // them — clear it so a different login in the same tab re-counts (bugbot).
    try {
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith('banner_viewed_')) sessionStorage.removeItem(key);
      }
    } catch {
      // sessionStorage unavailable — nothing stored, nothing to clear
    }
    // Logout starts a fresh anonymous session — allow a new landing window so
    // post-logout content browsing is tracked again (referrer is internal, so
    // it can never fake an organic seo_landing).
    seoLandingHandled = false;
    pendingFirstLanding = null;
    recordingSuppressedByRoute = false;
    try {
      sessionStorage.removeItem(SEO_LANDING_SS_KEY);
    } catch {
      // Storage unavailable — module flag reset above is enough.
    }
    // Restore the recording override before anything can opt back in:
    // optIn()/identify() arm recording via startSessionRecording, an
    // in-memory config flip that posthog.reset() does NOT undo. Without
    // this, the next anonymous cookieless opt-in (trackContentView) would
    // inherit the armed recorder and replay a logged-out visitor.
    posthog.stopSessionRecording();
    posthog.reset();
    // posthog.reset() wipes super properties — restore them so a later login
    // in the same tab still stamps platform/app_version/environment.
    registerSuperProperties();
    posthog.opt_out_capturing();
  },
};

export default PostHogUtils;
